/** Normalize API status values (e.g. "rejected", "Changes Required") to enum-style keys. */
export function normalizeDiscussionStatusValue(status: unknown): string {
  if (status == null || status === '') return '';
  return String(status).trim().toUpperCase().replace(/\s+/g, '_');
}

/** Latest review status from discussion_reviews when top-level status is absent on getReview(). */
export function extractLatestReviewStatus(reviews: unknown): string | null {
  if (!Array.isArray(reviews) || reviews.length === 0) return null;
  const sorted = [...reviews].sort((a: any, b: any) => {
    const ta = new Date(a?.reviewed_at ?? a?.created_at ?? 0).getTime();
    const tb = new Date(b?.reviewed_at ?? b?.created_at ?? 0).getTime();
    return tb - ta;
  });
  const latest = sorted[0];
  const raw =
    latest?.review_status ?? latest?.status ?? latest?.reviewStatus ?? null;
  if (raw == null || String(raw).trim() === '') return null;
  return normalizeDiscussionStatusValue(raw);
}

/** Resolve discussion status from API payload (getReview may omit status; reviews carry it). */
export function resolveDiscussionStatus(data: any): string | undefined {
  const fromReviews = extractLatestReviewStatus(data?.discussion_reviews);
  // Latest rejection review is authoritative over stale top-level status fields.
  if (fromReviews === 'REJECTED') return 'REJECTED';

  const candidates = [
    data?.status,
    data?.review_status,
    data?.discussion_status,
    data?.discussion?.status,
    data?.discussion?.review_status,
  ];
  for (const candidate of candidates) {
    if (candidate != null && String(candidate).trim() !== '') {
      return normalizeDiscussionStatusValue(candidate);
    }
  }
  return fromReviews || undefined;
}

/** True when API payload represents a rejected discussion. */
export function isDiscussionRejectedStatus(
  data: any,
  latestReviewStatus?: string | null,
): boolean {
  const reviewStatus =
    latestReviewStatus ?? extractLatestReviewStatus(data?.discussion_reviews);
  if (reviewStatus === 'REJECTED') return true;
  return normalizeDiscussionStatusValue(resolveDiscussionStatus(data)) === 'REJECTED';
}
